import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import t from "@/lang/lang";
import { AhuComponentsSchema } from "@/services/backend/htz/ahu/components/ahu-components-schema";
import { useState } from "react";
import {
  PutRequest,
  useHtzAirHandlingUnitDataPutMutation,
} from "@/services/backend/htz/ahu/api/ahu-data-api";
import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { AhuComponentsTable } from "@/services/backend/htz/ahu/components/ahu-components-table";
import {
  useHtzAhuComponentCatalogShowActiveCatalogQuery,
  useHtzAhuComponentCatalogShowQuery,
} from "@/services/backend/htz/ahu/api/ahu-component-catalog-api";
import { RefreshCw } from "lucide-react";
import { AddComponentDropdown } from "@/services/backend/htz/ahu/components/add-component-dropdown";
import { AirHandlingUnitData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";

export function AhuComponents({ ahu }: { ahu: AirHandlingUnitData }) {
  const [tabValue, setTabValue] = useState<string>("schema");
  const {
    data: activeCatalog,
    isLoading: activeCatalogIsLoading,
    error: activeCatalogError,
  } = useHtzAhuComponentCatalogShowActiveCatalogQuery(
    { companyId: ahu.companyId },
    { skip: !ahu.companyId },
  );
  const {
    data: catalog,
    isLoading: catalogIsLoading,
    error: catalogError,
  } = useHtzAhuComponentCatalogShowQuery(
    { id: activeCatalog?.catalogId ?? "" },
    { skip: !activeCatalog || activeCatalog.catalogId === null },
  );

  const [request, setRequest] = useState<PutRequest>({ ...ahu });
  const [put, { isLoading, error }] = useHtzAirHandlingUnitDataPutMutation();

  if (activeCatalogError || catalogError) {
    return <RTKQueryErrorAlert error={activeCatalogError || catalogError} />;
  }

  if (activeCatalogIsLoading || catalogIsLoading) {
    return <Skeleton />;
  }

  const doUpdate = () => {
    if (isLoading) {
      return;
    }

    put(request);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Komponenten")}</CardTitle>
        <CardDescription>
          {t(
            "Die Komponenten der RLT-Anlage können als Liste, wie auch als Schema verwaltet werden. Bitte beachten Sie, dass wenn eine Komponente in der Liste hinzugefügt wird, diese im Schema noch an die passende Position platziert werden muss.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={tabValue} onValueChange={setTabValue}>
          <div className="flex justify-between">
            <TabsList>
              <TabsTrigger value="schema">{t("Schema")}</TabsTrigger>
              <TabsTrigger value="list">{t("Liste")}</TabsTrigger>
            </TabsList>
            {tabValue === "list" && (
              <AddComponentDropdown
                ahuData={request}
                onAhuDataChange={setRequest}
                catalog={catalog!}
              />
            )}
          </div>
          <TabsContent value="schema">
            <AhuComponentsSchema
              ahuData={request}
              onAhuDataChange={setRequest}
              ahuComponentCatalog={catalog!}
              className="h-[600px]"
            />
          </TabsContent>
          <TabsContent value="list">
            <AhuComponentsTable ahu={request} onAhuChange={setRequest} />
          </TabsContent>
        </Tabs>
        <RTKQueryErrorAlert
          error={error}
          ignoreValidationError={false}
          className="mt-2"
        />
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={doUpdate}>{t("Speichern")}</Button>
      </CardFooter>
    </Card>
  );
}

function Skeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Komponenten")}</CardTitle>
        <CardDescription>
          {t(
            "Die Komponenten der RLT-Anlage können als Liste, wie auch als Schema verwaltet werden. Bitte beachten Sie, dass wenn eine Komponente in der Liste hinzugefügt wird, diese im Schema noch an die passende Position platziert werden muss.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RefreshCw className="animate-spin" />
      </CardContent>
    </Card>
  );
}
